// components/AvatarUpload/index.js - Versão ajustada
import React, { useContext, useState } from 'react';
import Avatar from '@material-ui/core/Avatar';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import CloudUploadIcon from '@material-ui/icons/CloudUploadOutlined';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getBackendUrl } from '../../config';
import { getTokens } from '../../styles/tokens';

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // foto à esquerda, ações e orientação à direita; empilha no celular
    root: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      minWidth: 0,
      [theme.breakpoints.down('xs')]: { flexDirection: 'column', alignItems: 'flex-start', gap: 12 },
    },
    avatar: {
      flex: 'none',
      width: 72,
      height: 72,
      cursor: 'pointer',
      color: t.textSecondary,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      transition: `box-shadow ${t.motion.fast}`,
      '&:hover, &:focus-visible': { boxShadow: t.shadow.glow, outline: 'none' },
    },
    body: { minWidth: 0 },
    title: {
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.text,
    },
    hint: {
      margin: '2px 0 0',
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: t.textSecondary,
    },
    actions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 8,
      marginTop: 10,
      '& label': { display: 'inline-flex' },
    },
  };
});

// `children`: ações extras ao lado do botão de envio (ex.: remover a foto atual).
const AvatarUploader = ({ setAvatar, avatar, companyId, children }) => {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const { user } = useContext(AuthContext);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Formato de arquivo não suportado. Use apenas JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validar tamanho (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    setSelectedFile(file);
    setAvatar(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para determinar qual imagem exibir
  const getImageSrc = () => {
    if (previewImage) return previewImage;
    if (avatar && companyId) return `${backendUrl}/public/company${companyId}/user/${avatar}`;
    return `${backendUrl}/public/app/noimage.png`;
  };

  const openPicker = () => document.getElementById('avatar-upload').click();

  return (
    <div className={classes.root}>
      <Avatar
        alt="Foto do usuário"
        src={getImageSrc()}
        className={classes.avatar}
        role="button"
        tabIndex={0}
        aria-label={avatar ? 'Alterar foto' : 'Enviar foto'}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onError={(e) => {
          // Fallback para imagem padrão em caso de erro
          e.target.src = `${backendUrl}/public/app/noimage.png`;
        }}
      />

      <div className={classes.body}>
        <div className={classes.title}>Foto do perfil</div>
        <p className={classes.hint}>JPG, PNG, GIF ou WebP, até 5 MB.</p>
        <div className={classes.actions}>
          <input
            accept="image/*"
            type="file"
            id="avatar-upload"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <label htmlFor="avatar-upload">
            <Button
              variant="outlined"
              size="small"
              component="span"
              startIcon={<CloudUploadIcon />}
            >
              {avatar ? 'Alterar foto' : 'Enviar foto'}
            </Button>
          </label>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AvatarUploader;
